import React from 'react';
import { RentalItem } from './RentalItem';
import { InfoIcon, AlertCircle, Wrench, ShieldCheck, AlertTriangle } from 'lucide-react';
export function RentalForm({
  selections,
  onSelectionChange,
  onTechToggle,
  onNumberOfDaysChange,
  prices,
  techOnlyMode = false,
  onTechOnlyToggle,
  isGen2Mode = false,
  onGen2Toggle
}) {
  const gen = isGen2Mode ? 'Gen 2' : 'Gen 3';
  const gen2Price = (price: number) => isGen2Mode ? price * 0.7 : price;
  return <div className="space-y-6" data-id="element-1326">
      {/* "I Already Have Gen 3" Section */}
      <div className={`rounded-lg border-2 transition-all ${techOnlyMode ? 'bg-amber-50 border-amber-400 shadow-md' : 'bg-white border-slate-200 shadow-md'}`} data-id="element-1327">
        <button type="button" onClick={onTechOnlyToggle} className="w-full p-5 flex items-start gap-4 text-left" data-id="element-1328">
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${techOnlyMode ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-1329">
            <Wrench size={24} data-id="element-1330" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1331">
            <div className="flex items-center gap-3 mb-1" data-id="element-1332">
              <h3 className={`text-lg font-bold ${techOnlyMode ? 'text-amber-900' : 'text-slate-800'}`} data-id="element-1333">
                I already have {gen} systems — I ONLY need a technician
              </h3>
            </div>
            <p className={`text-sm ${techOnlyMode ? 'text-amber-700' : 'text-slate-500'}`} data-id="element-1334">
              Select this if you own {gen} Daedo equipment and only need an
              on-site technician for your event.
            </p>
          </div>
          <div className="flex-shrink-0 mt-1" data-id="element-1335">
            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${techOnlyMode ? 'bg-amber-500 border-amber-500' : 'border-slate-300 bg-white'}`} data-id="element-1336">
              {techOnlyMode && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-1337">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1338" />
                </svg>}
            </div>
          </div>
        </button>
        {techOnlyMode && <div className="px-5 pb-5 pt-0" data-id="element-1339">
            <div className="flex items-start gap-2 p-3 bg-amber-100 border border-amber-300 rounded-lg" data-id="element-1340">
              <ShieldCheck size={18} className="text-amber-700 flex-shrink-0 mt-0.5" data-id="element-1341" />
              <p className="text-sm text-amber-800" data-id="element-1342">
                <strong data-id="element-1343">Tech-only mode active.</strong> Equipment selection is
                disabled. You'll only be charged the technician fee of{' '}
                <strong data-id="element-1344">${prices.technician.toFixed(2)}/day</strong>. A
                representative will verify your equipment for sanctioned event
                approval.
              </p>
            </div>
          </div>}
      </div>

      {/* Equipment Selection Form */}
      <div className={`bg-white p-6 rounded-lg shadow-md transition-all ${techOnlyMode ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-1345">
        <div className="flex items-center justify-between mb-6" data-id="element-1346">
          <h2 className="text-xl font-semibold text-blue-800" data-id="element-1347">
            Select Your Equipment
          </h2>
          <button type="button" onClick={onGen2Toggle} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${isGen2Mode ? 'bg-orange-100 border-orange-400 text-orange-800' : 'bg-slate-50 border-slate-300 text-slate-600 hover:border-slate-400'}`} data-id="element-1348">
            <div className={`w-8 h-4 rounded-full relative transition-all ${isGen2Mode ? 'bg-orange-500' : 'bg-slate-300'}`} data-id="element-1349">
              <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${isGen2Mode ? 'left-[18px]' : 'left-0.5'}`} data-id="element-1350" />
            </div>
            {isGen2Mode ? 'Gen 2 Mode' : 'Click to rent Gen 2'}
          </button>
        </div>

        {/* Gen 2 Warning Banner */}
        {isGen2Mode && <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-300 rounded-lg" data-id="element-1351">
            <div className="flex items-start gap-3" data-id="element-1352">
              <AlertTriangle size={20} className="text-orange-600 flex-shrink-0 mt-0.5" data-id="element-1353" />
              <div data-id="element-1354">
                <p className="text-sm font-bold text-orange-900 mb-1" data-id="element-1355">
                  Gen 2 Equipment Selected — 30% Reduced Pricing
                </p>
                <p className="text-xs text-orange-800" data-id="element-1356">
                  Gen 2 equipment{' '}
                  <strong data-id="element-1357">cannot be used for USAT sanctioned events</strong>. If
                  your event requires USAT sanctioning, please switch back to
                  Gen 3. All equipment prices are reduced by 30% for Gen 2
                  rentals.
                </p>
              </div>
            </div>
          </div>}

        {techOnlyMode && <div className="mb-6 p-4 bg-slate-100 border border-slate-300 rounded-lg text-center" data-id="element-1358">
            <p className="text-sm text-slate-600 font-medium" data-id="element-1359">
              Equipment selection is disabled — tech-only mode is active
            </p>
          </div>}

        {/* Tournament Equipment Group */}
        <div className="mb-8" data-id="element-1360">
          <h3 className="text-lg font-medium text-blue-800 mb-4 pb-2 border-b border-slate-200" data-id="element-1361">
            Tournament Equipment
          </h3>
          <div className="space-y-4" data-id="element-1362">
            <RentalItem title={`Full ${gen} Ring`} description="Includes two judges and size 0-4 hogus (Head not included - Most common starting point)" price={gen2Price(prices.fullGen3Ring)} value={selections.fullGen3Ring} onChange={value => onSelectionChange('fullGen3Ring', value)} highlight={true} disabled={techOnlyMode} data-id="element-1363" />
            <RentalItem title={`${gen} Head`} description={`Includes sizes S-L (Requires at least one ${gen} Body)`} price={gen2Price(prices.gen3Head)} value={selections.gen3Head} onChange={value => onSelectionChange('gen3Head', value)} disabled={techOnlyMode || selections.gen3Body + selections.fullGen3Ring === 0} maxValue={selections.gen3Body + selections.fullGen3Ring} validationMessage={selections.gen3Body + selections.fullGen3Ring === 0 ? `At least one ${gen} Body or Full ${gen} Ring is required` : `Maximum ${selections.gen3Body + selections.fullGen3Ring} allowed`} data-id="element-1364" />
          </div>
        </div>

        {/* Training Equipment Group */}
        <div className="mb-8" data-id="element-1365">
          <h3 className="text-lg font-medium text-blue-800 mb-4 pb-2 border-b border-slate-200" data-id="element-1366">
            Training Equipment
          </h3>
          <div className="space-y-4" data-id="element-1367">
            <RentalItem title={`${gen} Body`} description="Includes sizes 0-4 without judges" price={gen2Price(prices.gen3Body)} value={selections.gen3Body} onChange={value => onSelectionChange('gen3Body', value)} disabled={techOnlyMode} data-id="element-1368" />
            <RentalItem title={`${gen} Judges Only`} description="Includes 2 judge boxes per ring" price={gen2Price(prices.gen3JudgesOnly)} value={selections.gen3JudgesOnly} onChange={value => onSelectionChange('gen3JudgesOnly', value)} priceDisplay={`$${gen2Price(prices.gen3JudgesOnly).toFixed(2)} per set`} disabled={techOnlyMode} data-id="element-1369" />
          </div>
        </div>

        {/* Onsite Prep Group */}
        <div className="mb-8" data-id="element-1370">
          <h3 className="text-lg font-medium text-blue-800 mb-4 pb-2 border-b border-slate-200" data-id="element-1371">
            Onsite Prep
          </h3>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-1372">
            <p className="text-sm text-slate-600" data-id="element-1373">
              Additional onsite preparation services will be listed here.
            </p>
          </div>
        </div>
      </div>

      {/* Technician & Days Section - Always accessible */}
      <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1374">
        {/* Technician Option */}
        <div data-id="element-1375">
          {!isGen2Mode && <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4" data-id="element-1376">
              <div className="flex items-start" data-id="element-1377">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-1378" />
                <div data-id="element-1379">
                  <h4 className="font-semibold text-red-800 mb-1" data-id="element-1380">
                    On-site Technician Required
                  </h4>
                  <p className="text-sm text-red-700" data-id="element-1381">
                    An on-site technician is mandatory for all Gen 3 equipment
                    rentals to ensure proper setup and operation.
                  </p>
                </div>
              </div>
            </div>}
          {isGen2Mode && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-1382">
              <div className="flex items-start" data-id="element-1383">
                <InfoIcon className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-1384" />
                <div data-id="element-1385">
                  <h4 className="font-semibold text-blue-800 mb-1" data-id="element-1386">
                    On-site Technician (Optional)
                  </h4>
                  <p className="text-sm text-blue-700" data-id="element-1387">
                    A technician is not required for Gen 2 equipment rentals,
                    but is available if you'd like on-site support.
                  </p>
                </div>
              </div>
            </div>}
          <div className="flex items-start mb-4" data-id="element-1388">
            <div className="flex items-center h-5" data-id="element-1389">
              <input id="requireTech" type="checkbox" checked={selections.requireTech} onChange={onTechToggle} disabled={techOnlyMode} className={`h-5 w-5 rounded border-slate-300 ${isGen2Mode ? 'text-blue-600 focus:ring-blue-500' : 'text-red-600 focus:ring-red-500'} ${techOnlyMode ? 'cursor-not-allowed' : ''}`} data-id="element-1390" />
            </div>
            <div className="ml-3 text-sm" data-id="element-1391">
              <label htmlFor="requireTech" className="font-medium text-slate-700" data-id="element-1392">
                {isGen2Mode ? 'Add' : 'Require'} On-site Technician{' '}
                {!isGen2Mode && <span className="text-red-600" data-id="element-1393">*</span>}
                {techOnlyMode && <span className="ml-2 text-xs text-amber-700 font-normal" data-id="element-1394">
                    (auto-selected in tech-only mode)
                  </span>}
              </label>
              <div className="flex items-center text-slate-500 mt-1" data-id="element-1395">
                <InfoIcon size={16} className="mr-1" data-id="element-1396" />
                <p data-id="element-1397">
                  ${prices.technician.toFixed(2)} per day
                  {!isGen2Mode && ' - Required for all rentals'}
                </p>
              </div>
            </div>
          </div>

          {/* Multiple Day Sparring Event Option */}
          <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-1398">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3" data-id="element-1399">
              <h4 className="font-semibold text-blue-800 mb-1 text-sm" data-id="element-1400">
                Multiple Day Sparring Event
              </h4>
              <p className="text-xs text-blue-700" data-id="element-1401">
                Select the number of consecutive sparring days for your event.
                {!techOnlyMode && ' Additional days (Day 2+) are charged at 50% of the regular equipment price, and '}
                {techOnlyMode ? 'T' : 't'}echnician fee is multiplied by the
                number of days.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-1402">
              <label htmlFor="numberOfDays" className="text-sm font-medium text-slate-700" data-id="element-1403">
                Number of sparring days:
              </label>
              <div className="flex items-center gap-3" data-id="element-1404">
                <input id="numberOfDays" type="number" min="1" max="7" value={selections.numberOfDays} onChange={e => onNumberOfDaysChange(parseInt(e.target.value) || 1)} className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center" data-id="element-1405" />
                <span className="text-sm text-slate-600" data-id="element-1406">
                  {selections.numberOfDays === 1 ? 'day' : 'days'} (max 7)
                </span>
              </div>
            </div>
            {selections.numberOfDays > 1 && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1407">
                <p className="text-xs text-blue-800" data-id="element-1408">
                  {techOnlyMode ? <>
                      <strong data-id="element-1409">Pricing:</strong> Technician fee: $
                      {(prices.technician * selections.numberOfDays).toFixed(2)}{' '}
                      ({selections.numberOfDays} days × $
                      {prices.technician.toFixed(2)})
                    </> : <>
                      <strong data-id="element-1410">Pricing:</strong> Day 1 at full price, Days 2-
                      {selections.numberOfDays} at 50% price each. Technician
                      fee: $
                      {(prices.technician * selections.numberOfDays).toFixed(2)}{' '}
                      ({selections.numberOfDays} days × $
                      {prices.technician.toFixed(2)})
                    </>}
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}